/*
 * Copyright The Adaptive Palette copyright holders
 * See the AUTHORS.md file at the top-level directory of this distribution and at
 * https://github.com/inclusive-design/adaptive-palette/raw/main/AUTHORS.md.
 *
 * Licensed under the New BSD license. You may not use this file except in
 * compliance with this License.
 *
 * You may obtain a copy of the License at
 * https://github.com/inclusive-design/adaptive-palette/blob/main/LICENSE
 */

import { VNode } from "preact";
import { html } from "htm/preact";
import { useState } from "preact/hooks";

import { adaptivePaletteGlobals, settingsSavedCount } from "../state/GlobalData";
import { isLocalHost } from "../core/OllamaApi";
import { ModalDialog } from "./ModalDialog";
import { ActionSearchGloss } from "../cells/ActionSearchGloss";
import { ActionSvgEntryField } from "../cells/ActionSvgEntryField";
import { SettingsDialog } from "../features/settings/SettingsDialog";
import { AboutMeDialog } from "../features/about-me/AboutMeDialog";
import { t } from "../i18n/I18n";
import "./SymbolEntryToolbar.scss";

export const SEARCH_DIALOG_ID = "searchSymbolDialog";
export const SVG_DIALOG_ID = "svgBuilderStringDialog";
export const SETTINGS_DIALOG_ID = "adjustSettingsDialog";
export const ABOUT_ME_DIALOG_ID = "aboutMeDialog";

type OpenDialogType = "search" | "svg" | "settings" | "aboutMe" | null;

/**
 * The triggers at the right of the top bar, and the dialogs they open: adding a symbol to
 * the message, adjusting the settings, and About Me.  About Me is offered on the
 * desktop version only.
 *
 * The triggers live outside the palette grid because no palette row has a spare column:
 * a trigger cell would have to shrink a neighbour.  `#topBar` in `index.scss` is what keeps
 * them to the right, opposite the message attributes.
 *
 * Each dialog body is mounted only while its dialog is open, so reopening starts from a
 * clean form without any explicit reset code.
 * @returns {VNode}
 */
export function SymbolEntryToolbar (): VNode {
  // Read so this draws again after a save. See `settingsSavedCount`.
  void settingsSavedCount.value;
  const [openDialog, setOpenDialog] = useState<OpenDialogType>(null);
  const { symbolSearch, svgBuilderString } = adaptivePaletteGlobals.config;
  // About Me is only of use where a model reads it: the hosted site has none, so notes
  // typed there would go into prompts that are never sent.
  const showAboutMe = isLocalHost();

  const close = () => setOpenDialog(null);

  // WebKit, unlike Chromium and Firefox, does not focus a <button> on mouse click, so
  // ModalDialog would capture the wrong opener to restore focus to on close. Focusing
  // explicitly here makes the trigger the opener on every engine.
  const open = (which: Exclude<OpenDialogType, null>) => (event: Event) => {
    (event.currentTarget as HTMLElement).focus();
    setOpenDialog(which);
  };

  return html`
    <div class="symbolEntryToolbar">
      ${symbolSearch.show && html`
        <button
          type="button"
          class="btn-command"
          aria-haspopup="dialog"
          onClick=${open("search")}>${t("toolbarSearch")}</button>
      `}
      ${svgBuilderString.show && html`
        <button
          type="button"
          class="symbolEntryToolbarDevTrigger"
          aria-haspopup="dialog"
          onClick=${open("svg")}>${t("toolbarSvg")}</button>
      `}
      <button
        type="button"
        class="btn-command"
        aria-haspopup="dialog"
        onClick=${open("settings")}>${t("toolbarSettings")}</button>
      ${showAboutMe && html`
        <button
          type="button"
          class="btn-command"
          aria-haspopup="dialog"
          onClick=${open("aboutMe")}>${t("toolbarAboutMe")}</button>
      `}

      ${symbolSearch.show && html`
        <${ModalDialog}
          id=${SEARCH_DIALOG_ID}
          title=${t("toolbarSearch")}
          isOpen=${openDialog === "search"}
          onClose=${close}>
          ${openDialog === "search" && html`<${ActionSearchGloss} onRequestClose=${close} />`}
        <//>
      `}
      ${svgBuilderString.show && html`
        <${ModalDialog}
          id=${SVG_DIALOG_ID}
          title=${t("toolbarSvg")}
          isOpen=${openDialog === "svg"}
          onClose=${close}>
          ${openDialog === "svg" && html`<${ActionSvgEntryField} onRequestClose=${close} />`}
        <//>
      `}
      <${ModalDialog}
        id=${SETTINGS_DIALOG_ID}
        title=${t("toolbarSettings")}
        isOpen=${openDialog === "settings"}
        onClose=${close}>
        ${openDialog === "settings" && html`<${SettingsDialog} onRequestClose=${close} />`}
      <//>
      ${showAboutMe && html`
        <${ModalDialog}
          id=${ABOUT_ME_DIALOG_ID}
          title=${t("toolbarAboutMe")}
          isOpen=${openDialog === "aboutMe"}
          onClose=${close}>
          ${openDialog === "aboutMe" && html`<${AboutMeDialog} onRequestClose=${close} />`}
        <//>
      `}
    </div>
  `;
}
